/**
 * AZ ÁTTEKINTŐ — az utolsó pillanat, amíg a papír még a kezében van.
 *
 * A `szkenneles-terv.md` §4 egész érvelése erre fut ki: a telefon azonnal
 * szól, mert utána a számla az irattartóban van. Ez a képernyő az a „most" —
 * itt derül ki az elszámolt oldalszám, a kimaradt hátoldal és a ferdén
 * fotózott lap, amíg mindhárom ingyen javítható.
 *
 * ── NEM NEGYEDIK FELÜLET ────────────────────────────────────────────────
 *
 * A `CLAUDE.md` §2 szerint új képernyő csak új tengelyre jár. Ez nem nyit
 * újat: a fotózás MŰVELET, és ez a művelet második fele. Ezért réteg a kamera
 * fölött, nem útvonal — és ezért marad a kamera MÖGÖTTE ÉLVE: az „Még
 * fotózok" egy kattintás, nem egy új `getUserMedia` (ami iOS-en lassú, és
 * újra engedélyt kérhet).
 *
 * ── A SORREND GOMBBAL MOZOG, NEM HÚZÁSSAL ───────────────────────────────
 *
 * ⚠️ Ez tudatos eltérés a „húzható sorrend" megfogalmazástól. A HTML5
 * `dragstart`/`drop` ÉRINTÉSRE NEM SÜL EL — mobil böngészőben egyáltalán nem
 * működik, és ez a felület elsődlegesen mobil (`CLAUDE.md` §5, „ügyfél PWA,
 * mobil-elsődleges"). Egy húzás, ami a felhasználók többségénél nem csinál
 * semmit, rosszabb, mint a hiánya: nem hibaüzenetet ad, hanem NÉMÁN nem
 * történik semmi, és a felhasználó azt hiszi, ügyetlen.
 *
 * A saját `pointerdown`-os húzás megírható, de egy külön kérdés — ott a
 * görgetés és a húzás szétválasztása a nehéz rész, nem a sorrend. Addig a
 * két nyíl MINDEN eszközön működik, billentyűzetről is, és képernyőolvasóval
 * is kimondható (`akadalymentesites.md`).
 *
 * ── A `cropped: false` JELZÉS ───────────────────────────────────────────
 *
 * A `CapturedPage` azért hordozza, mert a kliensben nincs naplózó, és a hírt
 * valakinek el kell vinnie. Itt ér célba: ha a lapélt nem találtuk meg, a kép
 * a NYERS képkocka — benne az asztal, a kéz, a szomszéd papír. Nem hiba, és
 * nem is tiltjuk: a kinyerés ilyenből is olvas. De a felhasználó az egyetlen,
 * aki MOST el tudja dönteni, rajta van-e a végösszeg.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_FILES_PER_UPLOAD } from '@/contracts/generated/upload-limits';
import { PagePreview } from './PagePreview';
import { fitsInOneRequest } from './session-limits';
import type { SessionPage } from './useCaptureSession';

/**
 * A SZÁM A MONDATBAN — a megosztott korlátból, nem kézzel beírva.
 *
 * A `MAX_FILES_PER_UPLOAD` a `packages/upload-limits`-ból másolódik ide
 * (`shared-sync.py`); ha egyszer 20-ról elmozdul, ez a mondat vele mozdul. Egy
 * beégetett „20" pont akkor válna hazuggá, amikor senki nem keresné.
 */
const MAX_PAGES_HINT = MAX_FILES_PER_UPLOAD;

interface ReviewSheetProps {
  pages: readonly SessionPage[];
  /** Új sorrend azonosítók szerint — a `useCaptureSession.reorder`. */
  onReorder: (ids: string[]) => void;
  onRemove: (id: string) => void;
  /** Vissza a kamerába, EZT a lapot újrafotózni. A helyén marad. */
  onRetake: (id: string) => void;
  /**
   * „VÁGÁS" (#89) — a lap újravágása az eredeti fotóból. Csak annál a lapnál
   * kínáljuk, aminek megvan az eredetije (a #89 előtti, visszatöltött
   * sorozatok lapjainál nincs).
   */
  onCrop: (id: string) => void;
  /** Vissza a kamerába, új lapot fotózni. */
  onBack: () => void;
  onSend: () => void;
}

export function ReviewSheet({
  pages,
  onReorder,
  onRemove,
  onRetake,
  onCrop,
  onBack,
  onSend,
}: ReviewSheetProps) {
  const { t } = useTranslation();
  /** A teljes képernyőn megnyitott lap (#87); `null` = a lista látszik. */
  const [openId, setOpenId] = useState<string | null>(null);
  const openIndex = pages.findIndex((page) => page.id === openId);
  /** A bélyegkép, ami a nézetet nyitotta — bezáráskor ide tér vissza a fókusz. */
  const opener = useRef<HTMLButtonElement | null>(null);
  const closePreview = useCallback(() => {
    setOpenId(null);
    requestAnimationFrame(() => opener.current?.focus());
  }, []);

  // ⚠️ A KORLÁTOT ITT IS MÉRJÜK, nem csak a kamerában. A `canAddPage` a
  // fotózás pillanatában néz, ez a KÉSZ sorozatot — és a kettő közt a lapok
  // mérete változhat (a kivágás után derül ki), illetve egy újrafotózott lap
  // nagyobb is lehet a réginél. Egy fél sorozat után kapott szerverhiba
  // sokkal drágább, mint ez a második összeadás (`session-limits.ts`).
  const limit = fitsInOneRequest(pages.map((page) => page.blob));
  const sendable = pages.length > 0 && limit === 'ok';

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= pages.length) {
      return;
    }
    const ids = pages.map((page) => page.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onReorder(ids);
  };

  return (
    <div className="review" role="dialog" aria-modal="true" aria-label={t('capture.review.title')}>
      <header className="review-head">
        <h2>{t('capture.review.title')}</h2>
        <p className="meta">{t('capture.review.hint')}</p>
      </header>

      <div className="review-list">
        {pages.length === 0 ? (
          <p className="review-empty">{t('capture.review.empty')}</p>
        ) : (
          <ol>
            {pages.map((page, index) => (
              <li className="review-page" key={page.id}>
                {/* A BÉLYEGKÉP GOMB (#87): koppintásra a lap teljes képernyőn
                    nyílik. Viktor telefonos próbája: „nem bírom megnyitni,
                    hogy ellenőrizzem, amit fényképeztem" — egy 56 px-es képen
                    a kis betűs sorok nem látszanak. */}
                <button
                  type="button"
                  className="review-thumb"
                  aria-label={t('capture.review.open', { number: index + 1 })}
                  onClick={(event) => {
                    opener.current = event.currentTarget;
                    setOpenId(page.id);
                  }}
                >
                  <img src={page.previewUrl} alt="" />
                </button>

                <div className="review-page-body">
                  <p className="review-page-number">
                    {t('capture.pageNumber', { number: index + 1 })}
                  </p>

                  {page.cropped ? null : (
                    <p className="review-warn">{t('capture.review.notCropped')}</p>
                  )}

                  <div className="review-page-actions">
                    <button type="button" onClick={() => onRetake(page.id)}>
                      {t('capture.review.retake')}
                    </button>
                    {page.original ? (
                      <button type="button" onClick={() => onCrop(page.id)}>
                        {t('capture.review.crop')}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => onRemove(page.id)}>
                      {t('capture.review.remove')}
                    </button>
                  </div>
                </div>

                {/* A KÉT NYÍL A SOR VÉGÉN, nem a bélyegképen: a bélyegkép
                    a lapot NYITJA meg (#87), a sorrend máshol dől el. A szélső lapokon a nyíl LETILTVA marad, nem
                    tűnik el — az eltűnő gomb ugráltatná a sort. */}
                <div className="review-move">
                  <button
                    type="button"
                    aria-label={t('capture.review.moveUp')}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={t('capture.review.moveDown')}
                    disabled={index === pages.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <footer className="review-foot">
        {/* A KORLÁT MONDATA A GOMB FÖLÖTT VAN, nem a gombon. A tiltott gomb
            megmondja, hogy nem megy; ez a sor megmondja, MIT csináljon —
            enélkül csak annyi látszana, hogy „elromlott". */}
        {limit === 'ok' ? null : (
          <p className="review-limit" role="alert">
            {limit === 'too-many-pages'
              ? t('capture.review.tooManyPages', { count: MAX_PAGES_HINT })
              : t('capture.review.tooLarge')}
          </p>
        )}

        <div className="review-foot-actions">
          <button type="button" className="pick pick-link" onClick={onBack}>
            {t('capture.review.back')}
          </button>
          <button type="button" className="pick pick-primary" disabled={!sendable} onClick={onSend}>
            {t('capture.review.send', { count: pages.length })}
          </button>
        </div>
      </footer>

      {openIndex === -1 ? null : (
        <PagePreview
          src={pages[openIndex].previewUrl}
          title={t('capture.pageNumber', { number: openIndex + 1 })}
          onClose={closePreview}
        />
      )}
    </div>
  );
}
